import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { CreateStoreDto, Store, UpdateStoreDto } from '@mrrmlab/shared-types';
import { useApiClient } from './api-client-context';

export const storeKeys = {
  all: ['stores'] as const,
  stores: () => [...storeKeys.all, 'store'] as const,
};

type MutationExtras<TData, TVars> = Omit<
  UseMutationOptions<TData, Error, TVars>,
  'mutationFn'
>;

export function useStores(
  options?: Omit<UseQueryOptions<Store[]>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<Store[]>({
    queryKey: storeKeys.stores(),
    queryFn: ({ signal }) => client.stores.list(signal),
    ...options,
  });
}

export function useCreateStore(options?: MutationExtras<Store, CreateStoreDto>) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<Store, Error, CreateStoreDto>({
    mutationFn: (dto) => client.stores.create(dto),
    ...options,
    onSuccess: (data, vars, ctx, meta) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      options?.onSuccess?.(data, vars, ctx, meta);
    },
  });
}

export function useUpdateStore(
  options?: MutationExtras<Store, { id: string; dto: UpdateStoreDto }>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<Store, Error, { id: string; dto: UpdateStoreDto }>({
    mutationFn: ({ id, dto }) => client.stores.update(id, dto),
    ...options,
    onSuccess: (data, vars, ctx, meta) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      options?.onSuccess?.(data, vars, ctx, meta);
    },
  });
}

export function useDeleteStore(options?: MutationExtras<void, string>) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.stores.delete(id),
    ...options,
    onSuccess: (data, id, ctx, meta) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      options?.onSuccess?.(data, id, ctx, meta);
    },
  });
}
